import { fetchActiveRegions } from "@/ServerActions/companies";
import {
  addEmployee,
  deleteEmployee,
  disableEmployee,
  enableEmployee,
  fetchEmployeeDetails,
  fetchEmployeeRoleTitle,
  fetchVendorEmployee,
  updateEmployeeRole,
  useGetUserEmployeeDetails,
} from "@/ServerActions/employees";
import { ApiResponse, EmployeeDetailsProp, RegionsProps } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const useFetchEmployee = (id: string, serviceId: string) => {
  return useQuery({
    queryKey: ["employees", "DotAdmin", id, serviceId], // ✅ FIXED
    queryFn: () =>
      fetchVendorEmployee({ vendor_id: id }, serviceId),
    enabled: Boolean(id && serviceId),
  });
};

export const useRegions = () => {
  return useQuery<ApiResponse<RegionsProps[]>>({
    queryKey: ["regions", "DotAdmin"],
    queryFn: () => fetchActiveRegions(),
    staleTime: 0,
  });
};


export const useDeleteEmployee = () => {
  const queryClient: any = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      serviceId,
    }: {
      data: any;
      serviceId: string;
    }) => deleteEmployee(data, serviceId),
    onSuccess: (_data, variables) => {
      const vendorId = variables?.data?.vendor_id;

      queryClient.invalidateQueries({
        queryKey: ["employees", "DotAdmin", vendorId, variables.serviceId],
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });
};

export const useDisableEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      serviceId,
    }: {
      data: any;
      serviceId: string;
    }) => disableEmployee(data, serviceId),
    onSuccess: (_data, variables) => {
      const vendorId = variables?.data?.vendor_id;

      queryClient.invalidateQueries({
        queryKey: ["employees", "DotAdmin", vendorId, variables.serviceId],
      });
    },
  });
};

export const useEnableEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      serviceId,
    }: {
      data: any;
      serviceId: string;
    }) => enableEmployee(data, serviceId),
    onSuccess: (_data, variables) => {
      const vendorId = variables?.data?.vendor_id;

      queryClient.invalidateQueries({
        queryKey: ["employees", "DotAdmin", vendorId, variables.serviceId],
      });
    },
  });
};

export const useGetEmployeeDetails = (data: {
  vendor_id: string;
  user_id: string;
}, serviceId: string) => {
  return useQuery<ApiResponse<EmployeeDetailsProp>>({
    queryKey: [
      "employee-details",
      "DotAdmin",
      data.vendor_id,
      data.user_id,
      serviceId,
    ],
    queryFn: () => fetchEmployeeDetails(data, serviceId),
    enabled: Boolean(data.vendor_id && data.user_id && serviceId),
  });
};

export const useFetchRoleTitle = () => {
  return useMutation({
    mutationFn: (serviceId: string) =>
      fetchEmployeeRoleTitle(serviceId),
    onError: (error) => {
      console.log(error);
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      serviceId,
    }: {
      data: any;
      serviceId: string;
    }) => updateEmployeeRole(data, serviceId),
    onSuccess: (_data, variables) => {
      const vendorId = variables?.data?.vendor_id;
      const userId = variables?.data?.user_id;

      queryClient.invalidateQueries({
        queryKey: ["employees", "DotAdmin", vendorId, variables.serviceId],
      });

      queryClient.invalidateQueries({
        queryKey: [
          "employee-details",
          "DotAdmin",
          vendorId,
          userId,
          variables.serviceId,
        ],
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });
};

export const useEmployeeUserDetails = (
  userId: string,
  serviceId?: string // ✅ optional
) => {
  return useQuery({
    queryKey: ["user-details", "DotAdmin", userId, serviceId], // ✅ include for caching
    queryFn: () =>
      useGetUserEmployeeDetails(userId, serviceId),
    enabled: Boolean(userId),
  });
};

export const useCreateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      serviceId,
    }: {
      data: any;
      serviceId: string;
    }) => addEmployee(data, serviceId),
    onSuccess: (_data, variables) => {
      const vendorId = variables?.data?.vendor_id;

      queryClient.invalidateQueries({
        queryKey: ["employees", "DotAdmin", vendorId, variables.serviceId],
      });
    },
  });
};